import { mailApiHeaders } from '../services/mailApiHeaders';
import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';

export function normalizeSlideWebpageUrl(raw: string): string {
  const s = raw.trim();
  if (!s) return '';
  try {
    const u = new URL(s.includes('://') ? s : `https://${s}`);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    u.hash = '';
    return u.toString();
  } catch {
    return '';
  }
}

export interface SlideWebpagePreviewApiResult {
  normalizedUrl: string;
  title: string;
  thumbMime: string;
  thumbBase64: string;
  usedOg: boolean;
  embedSrc: string | null;
}

function previewApiUrl(): string {
  const base = (process.env.REACT_APP_PREVIEW_API_ORIGIN ?? '').trim().replace(/\/$/, '');
  return base ? `${base}/api/slide-webpage-preview` : '/api/slide-webpage-preview';
}

export async function fetchSlideWebpagePreview(url: string): Promise<SlideWebpagePreviewApiResult> {
  const res = await fetch(previewApiUrl(), {
    method: 'POST',
    headers: await mailApiHeaders(),
    body: JSON.stringify({ url }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(err?.error || `미리보기를 가져오지 못했습니다. (${res.status})`);
  }
  return (await res.json()) as SlideWebpagePreviewApiResult;
}

/** API·로컬 placeholder 응답을 WebP Blob으로 변환 (최대 가로 px) */
export async function thumbBase64ToWebpBlob(
  mime: string,
  base64: string,
  maxWidth = 600
): Promise<Blob> {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const srcBlob = new Blob([bytes], { type: mime || 'image/jpeg' });

  if (typeof createImageBitmap === 'undefined') {
    return srcBlob;
  }

  const bitmap = await createImageBitmap(srcBlob);
  const scale = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return srcBlob;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  const webp = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/webp', 0.82);
  });
  if (webp) return webp;
  return srcBlob;
}

/** 다른 슬라이드·블럭에 동일 URL 썸네일이 있으면 재사용 */
export function findWebpageThumbSrcForUrl(
  layouts: Record<number, ProgramSlideLayoutV1>,
  normalizedUrl: string
): string | null {
  if (!normalizedUrl) return null;
  for (const layout of Object.values(layouts)) {
    if (!layout?.webpages?.length) continue;
    for (const wp of layout.webpages) {
      if (normalizeSlideWebpageUrl(wp.url) !== normalizedUrl) continue;
      const thumb = wp.thumbSrc?.trim() ?? '';
      if (thumb) return thumb;
    }
  }
  return null;
}

export function localPlaceholderPreview(url: string): SlideWebpagePreviewApiResult {
  const normalizedUrl = normalizeSlideWebpageUrl(url);
  let host = 'link';
  let title = url.trim();
  try {
    const u = new URL(normalizedUrl);
    host = u.hostname.replace(/^www\./, '');
    title = host;
  } catch {
    /* ignore */
  }
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="600" height="340" viewBox="0 0 600 340">
  <rect width="600" height="340" fill="#eef2f6"/>
  <text x="48" y="120" font-family="system-ui,sans-serif" font-size="20" fill="#2a3140">${host}</text>
</svg>`;
  return {
    normalizedUrl,
    title,
    thumbMime: 'image/svg+xml',
    thumbBase64: btoa(unescape(encodeURIComponent(svg))),
    usedOg: false,
    embedSrc: null,
  };
}
